import { keyManager } from "@dhx/ts-common/Keymanager";
import { GridEvents, IGrid, ISelectionType, IDirection } from "./types";
import { isIE } from "@dhx/ts-common/html";

const focusHandler = {
	focusedID: undefined,
	isFocus: (id) => this.focusedID === id,
	getFocusState: () => this.focusedID,
	setFocusState: (state: string) => this.focusedID = state
};

export function initHotkeys(grid: IGrid) {
	const gridContainer = grid.getRootView().data.getRootNode();
	const gridBody = grid.getRootView().refs.grid_body.el;
	const gridId = gridContainer.getAttribute("dhx_widget_id");
	focusHandler.setFocusState(gridId);

	if (!grid.config.keyNavigation) {
		return;
	}

	const variables = {
		arrowLeft: isIE() ? "left" : "arrowLeft",
		arrowRight: isIE() ? "right" : "arrowRight",
		arrowUp: isIE() ? "up" : "arrowUp",
		arrowDown: isIE() ? "down" : "arrowDown",
		escape: isIE() ? "esc" : "escape",
		space: isIE() ? "spacebar" : "space"
	};

	const focusableKeyManager = {
		addHotKey(key, handler) {
			keyManager.addHotKey(key, e => {
				if (focusHandler.isFocus(gridId)) {
					handler(e);
				}
			});
		}
	};

	const isChild = (parent, child) => {
		let node = child.parentNode;
		while (node !== null) {
			if (node === parent) {
				return true;
			}
			node = node.parentNode;
		}
		return false;
	};

	gridContainer.addEventListener("mousedown", (e) => {
		const target = e.target as HTMLElement;
		if (isChild(gridContainer, target) || target.isEqualNode(gridContainer)) {
			focusHandler.setFocusState(gridId);
		}
	});

	focusableKeyManager.addHotKey("enter", () => {
		if (cellSelecting(grid.config.selection)) {
			const selected = grid.selection.getCell();
			if (selected.column.editing !== false && grid.config.editing || selected.column.editing) {
				if (!grid.config.$editable) {
					if (selected.column.type !== "boolean") {
						grid.edit(selected.row.id, selected.column.id, selected.column.editorType);
					} else {
						grid.events.fire(GridEvents.afterEditEnd, [!selected.row[selected.column.id], selected.row, selected.column]);
					}
				} else {
					grid.editEnd();
				}
			}
		}
	});

	focusableKeyManager.addHotKey(variables.space, e => {
		if (cellSelecting(grid.config.selection) && grid.config.editing && !grid.config.$editable) {
			const selected = grid.selection.getCell();
			if (selected.column.type === "boolean") {
				e.preventDefault();
				grid.events.fire(GridEvents.afterEditEnd, [!selected.row[selected.column.id], selected.row, selected.column]);
			}
		}
	});
	focusableKeyManager.addHotKey(variables.escape, () => {
		if (grid.config.$editable) {
			grid.editEnd(true);
		}
	});

	focusableKeyManager.addHotKey("pageUp", e => {
		e.preventDefault();
		gridBody.scrollTop -= gridBody.clientHeight;
	});
	focusableKeyManager.addHotKey("pageDown", e => {
		e.preventDefault();
		gridBody.scrollTop += gridBody.clientHeight;
	});

	focusableKeyManager.addHotKey("home", e => {
		e.preventDefault();
		gridBody.scrollTop = 0;
	});
	focusableKeyManager.addHotKey("end", e => {
		e.preventDefault();
		gridBody.scrollTop += gridBody.scrollHeight;
	});

	focusableKeyManager.addHotKey("tab", e => {
		selectionMove(e, grid, "horizontal", 1);
	});
	focusableKeyManager.addHotKey("shift+tab", e => {
		selectionMove(e, grid, "horizontal", -1);
	});

	focusableKeyManager.addHotKey(variables.arrowUp, e => {
		selectionMove(e, grid, "vertical", -1);
	});
	focusableKeyManager.addHotKey(variables.arrowDown, e => {
		selectionMove(e, grid, "vertical", 1);
	});
	focusableKeyManager.addHotKey(variables.arrowLeft, e => {
		selectionMove(e, grid, "horizontal", -1);
	});
	focusableKeyManager.addHotKey(variables.arrowRight, e => {
		selectionMove(e, grid, "horizontal", 1);
	});

	focusableKeyManager.addHotKey("ctrl+" + variables.arrowLeft, () => {
		selectionMove(null, grid, "horizontal", -1, true);
	});
	focusableKeyManager.addHotKey("ctrl+" + variables.arrowRight, () => {
		selectionMove(null, grid, "horizontal", 1, true);
	});
	focusableKeyManager.addHotKey("ctrl+" + variables.arrowUp, () => {
		selectionMove(null, grid, "vertical", -1, true);
	});
	focusableKeyManager.addHotKey("ctrl+" + variables.arrowDown, () => {
		selectionMove(null, grid, "vertical", 1, true);
	});
}

function cellSelecting(selection: ISelectionType) {
	return selection === "cell" || selection === "complex";
}

function selectionMove(e: KeyboardEvent, grid: IGrid, dir: IDirection, range: number, toEnd?: boolean) {
	if (grid.config.$editable || !grid.config.selection) {
		return;
	}
	if (e) {
		e.preventDefault();
	}
	const selected = grid.selection.getCell();
	if (selected) {
		if (dir === "vertical") {
			if (toEnd) {
				const newItem = range === 1 ? grid.data.getItem(grid.data.getId(grid.data.getLength() - 1)) : grid.data.getItem(grid.data.getId(0));
				grid.selection.setCell(newItem.id, selected.column.id.toString());
				grid.scrollTo(newItem.id, selected.column.id.toString());
			} else {
				const index = grid.data.getIndex(selected.row.id.toString());
				if (index + range >= 0 && index + range < grid.data.getLength()) {
					const newItem = grid.data.getItem(grid.data.getId(index + range));
					grid.selection.setCell(newItem.id, selected.column.id.toString());
					grid.scrollTo(newItem.id, selected.column.id.toString());
				}
			}
		} else {
			if (toEnd) {
				const newItem = range === 1 ? grid.config.columns[grid.config.columns.length - 1] : grid.config.columns[0];
				grid.selection.setCell(selected.row.id.toString(), newItem.id.toString());
				grid.scrollTo(selected.row.id.toString(), newItem.id.toString());
			} else {
				const index = grid.config.columns.indexOf(selected.column);
				if (index + range >= 0 && index + range < grid.config.columns.length) {
					grid.selection.setCell(selected.row.id.toString(), grid.config.columns[index + range].id.toString());
					grid.scrollTo(selected.row.id.toString(), grid.config.columns[index + range].id.toString());
				}
			}
		}
	}
}

